import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { catchError, map, of } from 'rxjs';
import { BaseForm } from '../../../../base-form';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { FormCommonApiService } from '../../../../../service/api/formcommon-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { build, buildParams, pool } from '../../../../../common/classes/request-builder';
import { FileUploadAnswer, Utils } from '../../../../../common/utils';
import { OtrasFormDefinition } from './form-control/form-definition';
import { environment } from '../../../../../../environments/environment';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { FormType, type FormField } from '../../../../../common/components/reactive-form-builder/interfaces/form.interface';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { UsuariosApiService } from '../../../../../service/api/usuarios-api.service';
import type { TareaOtraModel } from 'src/app/models/tareas/tarea-otra.model';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { MantenimientoModel } from 'src/app/models/trabajadores/mantenimiento.model';
import type { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import type { DropdownData } from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import type {
    ReactiveFormChangedField,
    ReactiveFormValue,
} from 'src/app/common/components/reactive-form-builder/interfaces/reactive-form-change.interface';

@Component({
    selector: 'app-otros-form',
    standalone: false,
    templateUrl: './otros-form.component.html',
    styleUrls: ['./otros-form.component.scss'],
    providers: [MessageService],
})
export class OtrosFormComponent extends BaseForm<TareaOtraModel> implements OnInit {
    public appName = environment.appName;
    public hasClients = environment.features.hasClients;
    public clienteName = environment.features.clienteName;
    public userCanSeeClientes = environment.features.userCanSeeClientes
        ? true
        : StorageManager.getUser().rol === 'admin';
    public horaFechaFinActualPorDefecto = environment.features.horaFechaFinActualPorDefecto;
    public showTareasOtrosDuracion = environment.features.showTareasOtrosDuracion;
    public showTareasOtrosImagen = environment.features.showTareasOtrosImagen;
    public horaFechaInicioActualPorDefecto = environment.features.horaFechaInicioActualPorDefecto;
    public activateRequireHourForRolUser = this.appName === 'ava' ? StorageManager.getUser().rol !== 'admin' : false;
    public now = new Date();
    public day = this.now.getDate().toString().padStart(2, '0');
    public month = (this.now.getMonth() + 1).toString().padStart(2, '0');
    public year = this.now.getFullYear().toString();
    public seleccionados = 0;
    public form!: OtrasFormDefinition;
    public override model: TareaOtraModel = {};
    public formReady = false;
    public loadingData = true;
    public idxMaquinaria = '';
    public adjunto: string | ArrayBuffer | null | undefined = null;
    public rolUser = StorageManager.getUser().rol;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public formApi: FormCommonApiService,
        public clientesApi: ClientesApiService,
        public usersApi: UsuariosApiService,
        public fincasApi: FincasApiService,
        public productosApi: ProductosApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public tareasApi: TareasApiService,
        private readonly messageService: MessageService,
        public override router: Router,
        public http: HttpClient,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.otras.PUT,
            tareasApi.otras.POST,
            'tareas-otros',
            'Actualizar Tarea',
            'Realizar Tarea Genérica',
            'Duplicar Tarea',
        );
    }

    public async ngOnInit(): Promise<void> {
        this.softInit(this.getType());
        this.model = { ...this.getModel() };

        if (this.horaFechaInicioActualPorDefecto) {
            this.model.fecha = `${this.day}/${this.month}/${this.year}`;
        }

        await this.loadEventosYForm();
        this.formReady = true;
    }

    public formChanges(
        formModel: ReactiveFormValue<TareaOtraModel>,
        lastChangedFieldName: ReactiveFormChangedField<TareaOtraModel>,
    ): void {
        const tag = String(lastChangedFieldName);

        this.form.model = {
            ...this.form.model,
            ...formModel,
        };
        this.model = this.form.model as TareaOtraModel;

        if (tag === 'hora_inicio' || tag === 'hora_fin') {
            this.updateHorasTotales();
        }

        if (this.model.id_maquinaria == null) {
            this.model.id_maquinaria = null;
        }

        if (tag === 'id_finca') {
            this.clearLocationChildren(['id_sector', 'ids_parcelas']);
        }

        if (tag === 'id_sector') {
            this.clearLocationChildren(['ids_parcelas']);
        }

        if (tag === 'id_cliente') {
            this.reloadClientLocations();
        }

        if (tag === 'ids_trabajadores') {
            this.syncMaquinariaFromTrabajador();
        }

        this.countTrabajadores();
        this.countSuperficieParcelas();
    }

    public afterSend(): void {
        if (this.getType() === FormType.DUPLICATION) {
            window.location.reload();
        }
    }

    public formatoHorasMinutos(horaEntrada: string, horaSalida: string): string {
        const [hours = '', minutes = ''] = horaEntrada.split(':');

        return moment(`${horaSalida} 2021-04-05`, 'HH:mm YYYY-MM-DD')
            .subtract({ hours: +hours, minutes: +minutes })
            .format('HH:mm');
    }

    public countTrabajadores(): void {
        const selectedTrabajadores = this.getSelectedTrabajadores();

        this.form.trabajadores.selected = selectedTrabajadores as never;
        this.seleccionados = selectedTrabajadores.length;
    }

    public countSuperficieParcelas(): void {
        const selectedParcelas = this.getSelectedParcelas();

        this.form.parcelas.selected = selectedParcelas as never;
        this.model.parcelas_superficie = Utils.decimalFormat(this.sumSuperficieParcelas(selectedParcelas), 2, '.', ',', 3);
        this.form.model.parcelas_superficie = this.model.parcelas_superficie;
    }

    public selectAll(): void {
        this.countSuperficieParcelas();
    }

    public override getItemPosition(formFields: FormField<object>[], field: string): number {
        let position = 0;

        formFields.forEach((item, index) => {
            if (item.name === field) {
                position = index;
            }
        });

        return position;
    }

    public getObservaciones(event: string): void {
        this.model.observaciones = event;
        this.form.model.observaciones = event;
        this.formBuilderRef?.updateForm({ observaciones: event }, true);
    }

    public comprobarHoraInicioFin(model: TareaOtraModel): boolean {
        if (model.hora_inicio == null || model.hora_fin == null) {
            this.messageService.add({ summary: 'Error en horas', detail: 'Hora inicio y hora fin son necesarias' });

            return true;
        }

        const fechaInicio = moment(model.hora_inicio).isValid()
            ? moment(model.hora_inicio).format('HH:mm')
            : model.hora_inicio ?? '';
        const fechaFin = moment(model.hora_fin).isValid()
            ? moment(model.hora_fin).format('HH:mm')
            : model.hora_fin?.toString() ?? '';

        if (fechaInicio === fechaFin) {
            this.messageService.add({ summary: 'Error en horas', detail: 'Hora inicio y hora fin no pueden ser iguales' });

            return true;
        }

        const resultInicio = this.timeToMilliseconds(fechaInicio);
        const resultFin = this.timeToMilliseconds(fechaFin);

        if (resultInicio >= resultFin) {
            this.messageService.add({
                summary: 'Error en horas',
                detail: 'Hora inicio y hora fin no pueden ser iguales, ni la hora fin anterior a la hora de inicio',
            });

            return true;
        }

        return false;
    }

    public milliseconds(h: number, m: number, s: number): number {
        return ((h * 60 * 60 + m * 60 + s) * 1000);
    }

    public readUrl(input: HTMLInputElement): void {
        input.click();

        if (!input.files?.[0]) {
            return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            this.adjunto = event.target?.result;
        };
        reader.readAsDataURL(input.files[0]);
        const request = this.http.post(environment.serverUrl + 'ws/seguimiento/upload_file.php', this.adjunto).subscribe((res) => {
            const file = res as FileUploadAnswer;

            this.model.adjunto = file.fileName;
            this.form.model.adjunto = file.fileName;
            this.formBuilderRef?.updateForm({ adjunto: file.fileName }, true);
            request.unsubscribe();
        });
    }

    public fileChange(event: Event): void {
        const input = event.target as HTMLInputElement;

        if (!input.files || input.files.length === 0) {
            return;
        }

        const file = input.files[0] ?? new File([], '');
        const formData = new FormData();

        formData.append('uploadFile', file, file.name);
        const headers = new HttpHeaders();

        headers.append('Content-Type', 'multipart/form-data');
        headers.append('Accept', 'application/json');

        const name = file.name.split('.');
        const ext = name[name.length - 1];
        const accepted = ['gif', 'jpeg', 'jpg', 'jiff', 'png', 'svg', 'tiff'];

        if (accepted.some((acceptedExt) => acceptedExt === ext)) {
            this.http.post<FileUploadAnswer>(
                `${environment.serverUrl}ws/productos/upload_file.php`,
                formData,
                { headers },
            ).pipe(
                map((data) => {
                    const res = data || {};

                    this.model.adjunto = res.fileName;
                    this.form.model.adjunto = res.fileName;
                    this.formBuilderRef?.updateForm({ adjunto: res.fileName }, true);
                }),
                catchError((error) => {
                    console.log(error);

                    return of(null);
                }),
            ).subscribe();
        } else {
            alert('Formato de imágen no válido.');
            this.model.adjunto = '';
            this.form.model.adjunto = '';
            this.formBuilderRef?.updateForm({ adjunto: '' }, true);
        }
    }

    public verAdjunto(): void {
        const myWindow = window.open('about:blank', '_blank', 'width=600, height=600');

        myWindow?.location.assign(environment.serverUrl + 'tareas_otros/' + this.model.adjunto);
        myWindow?.focus();
    }

    public async getEventosAPI(): Promise<{
        dates: Date[];
        eventos: { start: string; title: string; hora_inicio: string; hora_fin: string }[];
    }> {
        const fechas: Date[] = [];
        let eventosRaw: { start: string; title: string; hora_inicio: string; hora_fin: string }[] = [];

        try {
            eventosRaw = await this.tareasApi.eventos.GET.toPromise() as unknown as {
                start: string;
                title: string;
                hora_inicio: string;
                hora_fin: string;
            }[];

            for (const event of eventosRaw) {
                const [day, month, year] = event.start.split('-').map(Number);

                if ([day, month, year].every(Number.isInteger)) {
                    fechas.push(new Date(year ?? 0, (month ?? 1) - 1, day ?? 1));
                }
            }
        } catch (error) {
            console.error('Error cargando eventos:', error);
        }

        return { dates: fechas, eventos: eventosRaw };
    }

    private async loadEventosYForm(): Promise<void> {
        const eventosData = environment.appName === 'detorres'
            ? await this.getEventosAPI()
            : { dates: [], eventos: [] };

        this.form = new OtrasFormDefinition(eventosData.dates, eventosData.eventos, this.getType(), this.model);
        this.form.addClientFieldForDuplicate();
        await this.initFormData();
        this.afterInitialDataLoad();
        this.form.rebuildReactiveForm();
    }

    private initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.dropdownsFinalized = false;

        const buildsRequest = [
            build(this.clientesApi.clientesGET, this.form.clientes, 'nombre'),
            build(this.fincasApi.fincasGET, this.form.fincas, 'nombre', true),
            build(this.fincasApi.sectoresGET, this.form.sectores, 'nombre', true),
            build(this.fincasApi.parcelasGET, this.form.parcelas, 'nombre', true),
            build(this.trabajadoresApi.maquinariaGET, this.form.maquinarias,
                (it: MaquinariaModel) => `${it.nombre} ${it.empresa ? ` - (${it.empresa || '...'})` : ''}`, true),
            build(this.trabajadoresApi.trabajadoresGET, this.form.trabajadores,
                (it: MantenimientoModel) => it.nombre + ' ' + (it.apellidos || '') + (it.maquinaria
                    ? ' - (' + (it.maquinaria || '...') + ')'
                    : ''),
                true, true),
            build(this.tareasApi.listaTareas.GET, this.form.tareas, 'nombre'),
            build(this.productosApi.productos.GET, this.form.productos, 'nombre'),
        ];

        if (environment.features.showAssignUserTasksFitosanitario || environment.features.showAssignUserInAllTasks) {
            buildsRequest.push(build(this.usersApi.usuariosGET, this.form.users, 'nombre'));
        }

        return pool(buildsRequest, () => {
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.sortPinnedTareas();
            this.countTrabajadores();
            this.countSuperficieParcelas();

            if (this.form.fincas.filtered.length === 2 && this.getType() === FormType.CREATION) {
                const finca = this.form.fincas.filtered[1]?.value as { id?: string | number } | null | undefined;

                this.model.id_finca = finca?.id?.toString() ?? '';
                this.form.model.id_finca = this.model.id_finca;
            }

            this.loadingData = false;
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();

            return true;
        }).catch((error: unknown) => {
            this.loadingData = false;
            this.dropdownsFinalized = true;
            this.dropdownLoadTrigger.next();
            console.error('Error loading otros form data.', error);

            return true;
        });
    }

    private afterInitialDataLoad(): void {
        if (this.horaFechaFinActualPorDefecto && this.getType() !== FormType.EDITION) {
            const fechaActual = new Date();

            if (this.appName !== 'sirga') {
                this.model.fecha = fechaActual as unknown as string;
                this.form.model.fecha = this.model.fecha;
            }

            this.model.hora_fin = fechaActual as unknown as string;
            this.form.model.hora_fin = this.model.hora_fin;
        }

        for (const row of this.form.maquinarias.filtered) {
            if (!row.value?.fecha_ultima_inspeccion) {
                continue;
            }

            const auxFechaCad = (row.value.fecha_ultima_inspeccion as string).split('/');

            if (parseInt(auxFechaCad[0] ?? '', 10) >= 2020) {
                auxFechaCad[2] = (parseInt(auxFechaCad[2] ?? '', 10) + 3).toString();
            } else {
                auxFechaCad[2] = (parseInt(auxFechaCad[2] ?? '', 10) + 5).toString();
            }

            const fechaCaducidad = auxFechaCad[0] + '/' + auxFechaCad[1] + '/' + auxFechaCad[2];

            if (moment(fechaCaducidad, 'DD/MM/YYYY').isBefore(moment())) {
                row.label = '** ' + row.label;
            } else if (
                moment(fechaCaducidad, 'DD/MM/YYYY').isSameOrAfter(moment()) &&
                moment(fechaCaducidad, 'DD/MM/YYYY').isBefore(moment().add(3, 'months'))
            ) {
                row.label = '* ' + row.label;
            }
        }

        if (environment.features.showTareasMto && this.rolUser === 'user') {
            this.model.prioridad = 'Realizada';
            this.form.model.prioridad = 'Realizada';
        }
    }

    private updateHorasTotales(): void {
        if (!environment.features.showTareasMto &&
            !environment.features.tareasHorasTotales &&
            !environment.features.showTareasOtrosDuracion) {
            return;
        }

        if (this.model.hora_inicio == null || this.model.hora_fin == null) {
            this.model.horas_totales = '';
            this.form.model.horas_totales = '';
            this.formBuilderRef?.updateForm({ horas_totales: '' }, false);

            return;
        }

        let horaFin = moment(this.model.hora_fin).format('HH:mm');
        let horaInicio = moment(this.model.hora_inicio).format('HH:mm');
        const timeFormat = new RegExp('^(2[0-3]|[01]?[0-9]):([0-5]?[0-9])$');

        if (!timeFormat.test(horaInicio)) {
            horaInicio = this.model.hora_inicio;
        }

        if (!timeFormat.test(horaFin)) {
            horaFin = this.model.hora_fin.toString();
        }

        const diferencia = this.formatoHorasMinutos(horaInicio, horaFin);

        this.model.horas_totales = diferencia;
        this.form.model.horas_totales = diferencia;
        this.formBuilderRef?.updateForm({ horas_totales: diferencia }, false);
    }

    private clearLocationChildren(fieldNames: string[]): void {
        const patch = fieldNames.reduce((result, fieldName) => ({ ...result, [fieldName]: null }), {} as Record<string, null>);

        this.form.model = {
            ...this.form.model,
            ...patch,
        };
        this.model = this.form.model as TareaOtraModel;
        this.formBuilderRef?.updateForm(patch, true);
        this.dropdownLoadTrigger.next();
    }

    private reloadClientLocations(): void {
        this.clearLocationChildren(['id_finca', 'id_sector', 'ids_parcelas']);

        pool([
            buildParams(this.fincasApi.fincasGET, { id_cliente: this.model.id_cliente }, this.form.fincas, 'nombre', true),
            buildParams(this.fincasApi.sectoresGET, { id_cliente: this.model.id_cliente }, this.form.sectores, 'nombre', true),
            buildParams(this.fincasApi.parcelasGET, { id_cliente: this.model.id_cliente }, this.form.parcelas, 'nombre', true),
        ], () => {
            this.form.fincas.filtered = this.form.fincas.values;
            this.form.sectores.filtered = [];
            this.form.parcelas.values = this.form.parcelas.values.filter((it) => it.label !== '...');
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.dropdownLoadTrigger.next();
        }).catch((error: unknown) => {
            console.log('catch en getPosition: ' + error);
        });
    }

    private syncMaquinariaFromTrabajador(): void {
        const selectedList = this.getSelectedTrabajadores();

        if (selectedList.length !== 1 || !selectedList[0]?.id_maquina) {
            return;
        }

        this.model.id_maquinaria = selectedList[0].id_maquina ?? '';
        this.form.model.id_maquinaria = this.model.id_maquinaria;
        this.formBuilderRef?.updateForm({ id_maquinaria: this.model.id_maquinaria }, true);
    }

    private sortPinnedTareas(): void {
        const pinned = ['ENMACETADO', 'CORTE MAQUINÁRIA', 'CORTE MANUAL'];

        this.form.tareas.filtered = [...this.form.tareas.filtered].sort((a, b) => {
            const aIdx = pinned.indexOf((a.label ?? '').toUpperCase());
            const bIdx = pinned.indexOf((b.label ?? '').toUpperCase());

            if (aIdx !== -1 && bIdx !== -1) {
                return aIdx - bIdx;
            }

            if (aIdx !== -1) {
                return -1;
            }

            if (bIdx !== -1) {
                return 1;
            }

            return (a.label ?? '').localeCompare(b.label ?? '');
        });
    }

    private getSelectedParcelas(): ParcelasModel[] {
        const rawValue = this.formBuilderRef?.model?.get('ids_parcelas')?.value ?? this.model.ids_parcelas ?? this.form.parcelas.selected;

        return this.getSelectedDropdownItems(this.form.parcelas, rawValue);
    }

    private getSelectedTrabajadores(): MantenimientoModel[] {
        const rawValue = this.formBuilderRef?.model?.get('ids_trabajadores')?.value ??
            this.model.ids_trabajadores ??
            this.form.trabajadores.selected;

        return this.getSelectedDropdownItems(this.form.trabajadores as DropdownData<MantenimientoModel>, rawValue);
    }

    private getSelectedDropdownItems<T extends { id?: string | number | null }>(dropdownData: DropdownData<T>, rawValue: unknown): T[] {
        const selectedItems = this.getRawSelectedItems<T>(rawValue);

        if (selectedItems.length > 0) {
            return selectedItems;
        }

        const ids = this.getSelectedIds(rawValue);

        if (ids.length === 0) {
            return [];
        }

        return (dropdownData.values ?? [])
            .map((option) => option.value)
            .filter((value): value is T => value != null && ids.includes(String(value.id ?? '')));
    }

    private getRawSelectedItems<T extends { id?: string | number | null }>(rawValue: unknown): T[] {
        const rawItems = Array.isArray(rawValue) ? rawValue : [rawValue];

        return rawItems.filter((item): item is T => this.isDropdownEntity(item));
    }

    private isDropdownEntity<T extends { id?: string | number | null }>(value: unknown): value is T {
        return typeof value === 'object' && value !== null && 'id' in value;
    }

    private getSelectedIds(rawValue: unknown): string[] {
        if (Array.isArray(rawValue)) {
            return rawValue
                .flatMap((item) => this.getSelectedIds(item))
                .filter((value, index, array) => value.length > 0 && array.indexOf(value) === index);
        }

        if (rawValue === null || rawValue === undefined || rawValue === '') {
            return [];
        }

        if (typeof rawValue === 'object') {
            const id = (rawValue as { id?: string | number | null }).id;

            return id === null || id === undefined || id === '' ? [] : [String(id)];
        }

        return String(rawValue)
            .split(';')
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
    }

    private sumSuperficieParcelas(parcelas: ParcelasModel[]): number {
        return parcelas
            .filter((it) => it && (it.superficie_cultivada || it.superficie_sigpac))
            .map((it) => parseFloat(((it.superficie_cultivada ?? 0) || (it.superficie_sigpac || 0)).toString().replace(',', '.')))
            .reduce((a, b) => a + b, 0);
    }

    private timeToMilliseconds(time: string): number {
        const [hours = '0', minutes = '0'] = time.split(':');

        return this.milliseconds(+hours, +minutes, 0);
    }
}
